import { useEffect, useState, type CSSProperties } from 'react'
import { topicPhoto, BRIAN_TOPIC_IDS } from '@/lib/topicPhotos'
import type { PicStory } from '@/lib/picMatch'
import { useStoryPhoto } from '@/hooks/useStoryPhoto'

/**
 * A topic's drawn scene (Brian's pics.js): a hill line, a sun and a few blocks in
 * the topic's own color, drawn once on a canvas and cached. It is the last
 * fallback for any picture: the outlet's photo, then the library photo matched
 * to the story (useStoryPhoto), then the topic's stock photo (topicPhoto), then this, which always draws, so no card is ever an empty box.
 *
 *   <TopicScene topic="events" />                      the scene alone, as an <img>
 *   <TopicImage topic="events" src={outletPhoto} story={s} />  photo → library photo → stock photo → scene
 *   style={{ backgroundImage: topicBackground(id) }}   stock photo over the scene (CSS)
 *   topicScene(id, seed?)                              the scene as a data: URL
 *
 * Accepts our taxonomy ids (food-drink) and Brian's short ids (food).
 */

// A color per topic, spread round the wheel so a page of topics isn't one shade
// of orange (Brian, Oct 21: more color, feel like social). [hue, saturation].
const HUES: Record<string, [number, number]> = {
  realestate: [28, 36], development: [210, 22], newbusiness: [262, 34], outdoor: [140, 30], events: [330, 44], civic: [200, 14],
  food: [10, 46], celebritynews: [300, 34], safety: [0, 34], weather: [205, 30], sports: [120, 26], localsports: [120, 26],
  prosports: [95, 30], health: [170, 28], schools: [45, 44], tourism: [185, 36], finance: [150, 20], agriculture: [75, 34],
  artistsbands: [280, 34], arts: [320, 32], aviation: [215, 34], cars: [230, 16], fitness: [160, 34], geopolitics: [225, 12],
  history: [35, 22], homegarden: [100, 32], jobs: [25, 28], military: [80, 12], nonprofits: [345, 30], politics: [235, 20],
  realitytv: [290, 40], roads: [40, 30], space: [250, 40], technology: [195, 40], truecrime: [260, 12],
}
const SHORT_OF: Record<string, string> = Object.fromEntries(Object.entries(BRIAN_TOPIC_IDS).map(([short, id]) => [id, short]))
const shortId = (topic: string) => SHORT_OF[topic] ?? topic

/**
 * Where each photo's subject sits, so a tile's crop keeps it (CSS
 * background-position; Brian's GG_TOPIC_POS). Empty in his latest build: every
 * photo crops from the centre (tiles) or the top centre (topic cards).
 */
export const TOPIC_POS: Record<string, string> = {}

/** The topic's hue, for anything that wants its color (a gradient, a ring). */
export function topicHue(topic: string | null | undefined): [number, number] {
  return HUES[shortId(topic ?? '')] ?? [30, 20]
}

const cache = new Map<string, string>()

/** The drawn scene as a data: URL. `seed` varies the hills per story; the
 *  default is the topic's own theme scene. '' when there is no canvas (tests). */
export function topicScene(topic: string | null | undefined, seed = 'theme'): string {
  const t = topic ?? 'x'
  const key = `${t}|${seed}`
  const hit = cache.get(key)
  if (hit) return hit
  if (typeof document === 'undefined') return ''
  const c = document.createElement('canvas')
  c.width = 720; c.height = 450
  const g = c.getContext('2d')
  if (!g) return ''
  const [hue, sat] = topicHue(t)
  const gr = g.createLinearGradient(0, 0, 720, 450)
  gr.addColorStop(0, `hsl(${hue} ${sat}% 34%)`); gr.addColorStop(1, `hsl(${hue + 30} ${sat}% 14%)`)
  g.fillStyle = gr; g.fillRect(0, 0, 720, 450)
  let n = 0
  for (const ch of seed + shortId(t)) n = (n * 31 + ch.charCodeAt(0)) % 997
  // the hills
  g.fillStyle = `hsl(${hue} ${sat}% 12%)`
  g.beginPath(); g.moveTo(0, 450); g.lineTo(0, 280 + (n % 40))
  for (let x = 0; x <= 720; x += 45) g.lineTo(x, 260 + ((n * (x + 7)) % 90))
  g.lineTo(720, 450); g.fill()
  // the sun
  g.fillStyle = `hsla(${hue + 20} 70% 70% / .55)`
  g.beginPath(); g.arc(560 - (n % 200), 120 + (n % 60), 38 + (n % 30), 0, 7); g.fill()
  // the blocks
  g.fillStyle = 'rgba(0,0,0,.28)'
  for (let i = 0; i < 5; i++) { const w = 60 + ((n * (i + 3)) % 90); g.fillRect((i * 150 + n) % 700, 330 - ((n * (i + 1)) % 80), w, 220) }
  g.fillStyle = 'rgba(255,255,255,.08)'; g.fillRect(0, 0, 720, 450)
  const url = c.toDataURL('image/jpeg', 0.8)
  cache.set(key, url)
  return url
}

/** CSS background-image: the stock photo over the drawn scene, so a photo that
 *  can't load leaves the scene showing (Brian's settings-topics tiles). */
export function topicBackground(topic: string | null | undefined): string {
  const photo = topicPhoto(topic)
  const scene = topicScene(topic)
  return [photo ? `url('${photo}')` : '', scene ? `url('${scene}')` : ''].filter(Boolean).join(', ')
}

/** The drawn scene alone. */
export function TopicScene({ topic, seed, className, style, alt = '' }: {
  topic: string | null | undefined
  seed?: string
  className?: string
  style?: CSSProperties
  alt?: string
}) {
  return <img className={className} style={style} src={topicScene(topic, seed)} alt={alt} draggable={false} />
}

/**
 * A picture that never comes up empty: `src` (the outlet's photo) if given,
 * then the library photo matched to `story` (when there is no `src`), then the
 * topic's stock photo, then the drawn scene (Brian's GGPic.fix).
 */
export function TopicImage({ topic, src, story, seed, className, style, alt = '', loading = 'lazy' }: {
  topic: string | null | undefined
  src?: string | null
  story?: PicStory | null
  seed?: string
  className?: string
  style?: CSSProperties
  alt?: string
  loading?: 'lazy' | 'eager'
}) {
  const lib = useStoryPhoto(src ? null : story)
  const chain = [src || lib, topicPhoto(topic)].filter((s): s is string => !!s)
  const [step, setStep] = useState(0)
  useEffect(() => { setStep(0) }, [src, lib, topic])
  const url = step < chain.length ? chain[step] : topicScene(topic, seed)
  return (
    <img className={className} style={style} src={url} alt={alt} loading={loading} draggable={false}
      onError={() => setStep((s) => (s < chain.length ? s + 1 : s))} />
  )
}
